import React, { useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Chip,
  Collapse,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ChangePassword from '../User/ChangePassword';

const drawerWidth = 260;

// Color mapping for menu icons
const iconColors = {
  'Project Management': '#7C4DFF',
  'Projects': '#7C4DFF',
  'SBL View': '#66BB6A',
  'Locations': '#00BCD4',
  'Holidays': '#FF7043',
  'Employees': '#5C6BC0',
  'Employee List': '#5C6BC0',
  'Leaves': '#26A69A',
  'Reports': '#AB47BC',
  'Invoices': '#42A5F5',
  'Billing Reports': '#AB47BC',
  'Batch Upload': '#FFA726',
  'User Management': '#EF5350',
  'My Team': '#EC407A',
  'SOW Report': '#FF5722',
  'Projection Comparison': '#7B1FA2',
};

const Layout = () => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    'Project Management': false,
    'Employees': false,
    'Reports': false,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuToggle = (menuText) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuText]: !prev[menuText]
    }));
  };

  // Define menu structure with nested items
  const menuStructure = [
    {
      text: 'Project Management',
      icon: <FolderIcon />,
      roles: ['Admin', 'PowerUser', 'SuperUser', 'User'],
      children: [
        { text: 'Projects', icon: <FolderIcon />, path: '/projects', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'Locations', icon: <LocationOnIcon />, path: '/locations', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'Holidays', icon: <EventIcon />, path: '/holidays', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
      ],
    },
    {
      text: 'Employees',
      icon: <PeopleIcon />,
      roles: ['Admin', 'PowerUser', 'SuperUser', 'User'],
      children: [
        { text: 'Employee List', icon: <PeopleIcon />, path: '/employees', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'Leaves', icon: <EventBusyIcon />, path: '/leaves', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
      ],
    },
    {
      text: 'Reports',
      icon: <AssessmentIcon />,
      roles: ['Admin', 'PowerUser', 'SuperUser', 'User'],
      children: [
        { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'Billing Reports', icon: <AssessmentIcon />, path: '/reports', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'SOW Report', icon: <TrendingUpIcon />, path: '/sow-report', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'Projection Comparison', icon: <CompareArrowsIcon />, path: '/projection-comparison', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
        { text: 'SBL View', icon: <ViewWeekIcon />, path: '/sbl-view', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
      ],
    },
    { text: 'Batch Upload', icon: <UploadFileIcon />, path: '/batch-upload', roles: ['Admin', 'PowerUser', 'SuperUser', 'User'] },
    { text: 'divider', icon: null, path: null, roles: ['Admin', 'SuperUser'] },
    { text: 'User Management', icon: <AdminPanelSettingsIcon />, path: '/users', roles: ['Admin'] },
    { text: 'My Team', icon: <GroupsIcon />, path: '/my-team', roles: ['SuperUser'] },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'error';
      case 'SuperUser': return 'secondary';
      case 'PowerUser': return 'primary';
      default: return 'default';
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #5C6BC0 0%, #7C4DFF 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" fontWeight="bold" sx={{ letterSpacing: 1 }}>
          SBL
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Service-Based Ledger
        </Typography>
      </Box>

      {/* User Info */}
      {user && (
        <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: '#5C6BC0',
                width: 40,
                height: 40,
              }}
            >
              <AccountCircleIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight="600">
                {user.username}
              </Typography>
              <Chip
                label={user.role}
                size="small"
                color={getRoleColor(user.role)}
                sx={{ height: 20, fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
          <Button
            size="small"
            startIcon={<VpnKeyIcon sx={{ fontSize: 16 }} />}
            onClick={() => setChangePasswordOpen(true)}
            sx={{
              mt: 1,
              fontSize: '0.75rem',
              color: '#64748B',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#F1F5F9',
              },
            }}
          >
            Change Password
          </Button>
        </Box>
      )}

      {/* Menu Items */}
      <List sx={{ flex: 1, py: 1 }}>
        {menuStructure.map((item, index) => {
          // Skip if user role doesn't have access
          if (!item.roles?.includes(user?.role)) return null;

          // Handle divider
          if (item.text === 'divider') {
            return <Divider key={`divider-${index}`} sx={{ my: 1.5, mx: 2 }} />;
          }

          const iconColor = iconColors[item.text] || '#5C6BC0';

          // If item has children, render as collapsible group
          if (item.children) {
            const isOpen = openMenus[item.text];
            const hasActiveChild = item.children.some(child => location.pathname === child.path);

            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding sx={{ px: 1.5, py: 0.3 }}>
                  <ListItemButton
                    onClick={() => handleMenuToggle(item.text)}
                    sx={{
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      backgroundColor: hasActiveChild ? `${iconColor}10` : 'transparent',
                      '&:hover': {
                        backgroundColor: '#F1F5F9',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: hasActiveChild ? iconColor : '#64748B',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight: hasActiveChild ? 600 : 500,
                        fontSize: '0.9rem',
                        color: hasActiveChild ? iconColor : '#475569',
                      }}
                    />
                    {isOpen ? <ExpandLess sx={{ color: '#64748B' }} /> : <ExpandMore sx={{ color: '#64748B' }} />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ backgroundColor: '#F8FAFC', py: 0.5 }}>
                    {item.children
                      .filter(child => child.roles?.includes(user?.role))
                      .map(child => {
                        const isChildSelected = location.pathname === child.path;
                        const childIconColor = iconColors[child.text] || '#5C6BC0';

                        return (
                          <ListItem key={child.text} disablePadding sx={{ pl: 4, pr: 1.5, py: 0.1 }}>
                            <ListItemButton
                              selected={isChildSelected}
                              onClick={() => handleNavigation(child.path)}
                              sx={{
                                borderRadius: 1.5,
                                py: 0.6,
                                transition: 'all 0.2s ease',
                                '&.Mui-selected': {
                                  backgroundColor: `${childIconColor}18`,
                                  borderLeft: `3px solid ${childIconColor}`,
                                  '&:hover': {
                                    backgroundColor: `${childIconColor}25`,
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: '#E2E8F0',
                                },
                              }}
                            >
                              {/* Small dot indicator instead of icon */}
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: isChildSelected ? childIconColor : '#94A3B8',
                                  mr: 1.5,
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={child.text}
                                primaryTypographyProps={{
                                  fontWeight: isChildSelected ? 600 : 400,
                                  fontSize: '0.78rem',
                                  color: isChildSelected ? childIconColor : '#64748B',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          // Regular menu item (no children)
          const isSelected = location.pathname === item.path;

          return (
            <ListItem key={item.text} disablePadding sx={{ px: 1.5, py: 0.3 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    backgroundColor: `${iconColor}15`,
                    borderLeft: `3px solid ${iconColor}`,
                    '&:hover': {
                      backgroundColor: `${iconColor}20`,
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#F1F5F9',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isSelected ? iconColor : '#64748B',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: '0.9rem',
                    color: isSelected ? iconColor : '#475569',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Logout Button */}
      <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0' }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{
            borderRadius: 2,
            py: 1,
          }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #5C6BC0 0%, #7C4DFF 100%)',
        }}
      >
        <Typography variant="h4" color="white">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F5F7FA', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(90deg, #5C6BC0 0%, #7C4DFF 50%, #AB47BC 100%)',
          borderBottom: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                S
              </Typography>
            </Box>
            <Typography variant="h6" noWrap fontWeight="600" sx={{ letterSpacing: 0.5 }}>
              Service-Based Ledger
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: '4px 0 24px rgba(0,0,0,0.06)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Change Password Dialog */}
      <ChangePassword
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
};

export default Layout;
